/**
 * 知识库相关的React Hooks
 * 基于SWR提供知识库管理相关的状态管理和API调用功能
 */

import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import {
  KNOWLEDGE_API_PATHS,
  knowledgeService,
} from '../services/knowledge.service'
import type {
  KnowledgeBase,
  GetKnowledgeListRequest,
  KnowledgeListResponse,
  SaveOrUpdateKnowledgeRequest,
  DeleteKnowledgeRequest,
  ToggleKnowledgeStateRequest,
} from '../types/knowledge.types'
import { KnowledgeStatus } from '../types/knowledge.types'

/**
 * 获取知识库列表Hook
 * @param params 查询参数（搜索关键词、工作空间ID等）
 * @returns SWR查询对象，包含data、error、mutate等
 */
export const useKnowledgeList = (params: GetKnowledgeListRequest | null) => {
  const key = params?.workspaceId
    ? `/knowledgeBase/list?workspaceId=${params.workspaceId}`
    : null

  return useSWR<KnowledgeListResponse, Error>(
    key,
    () =>
      params
        ? knowledgeService.getKnowledgeList(params)
        : Promise.reject(new Error('No params')),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )
}

/**
 * 新增或编辑知识库Hook
 * @returns SWR mutation对象，包含trigger（保存函数）、isMutating（加载状态）、error（错误信息）
 */
export const useSaveOrUpdateKnowledge = () => {
  return useSWRMutation<
    KnowledgeBase,
    Error,
    string,
    SaveOrUpdateKnowledgeRequest
  >(KNOWLEDGE_API_PATHS.SAVE_OR_UPDATE, (_, { arg }) =>
    knowledgeService.saveOrUpdateKnowledge(arg)
  )
}

/**
 * 删除知识库Hook
 * @returns SWR mutation对象，包含trigger（删除函数）、isMutating（加载状态）、error（错误信息）
 */
export const useDeleteKnowledge = () => {
  return useSWRMutation<boolean, Error, string, DeleteKnowledgeRequest>(
    KNOWLEDGE_API_PATHS.DELETE,
    (_, { arg }) => knowledgeService.deleteKnowledge(arg)
  )
}

/**
 * 切换知识库状态Hook
 * @returns SWR mutation对象，包含trigger（切换函数）、isMutating（加载状态）、error（错误信息）
 */
export const useToggleKnowledgeState = () => {
  return useSWRMutation<boolean, Error, string, ToggleKnowledgeStateRequest>(
    KNOWLEDGE_API_PATHS.TOGGLE_STATE,
    (_, { arg }) => knowledgeService.toggleKnowledgeState(arg)
  )
}

/**
 * 知识库操作组合Hook
 * 提供完整的知识库管理功能，包括列表获取、新增、编辑、删除、状态切换
 * @param params 列表查询参数
 * @returns 包含所有知识库操作方法的对象
 */
export const useKnowledge = (params: GetKnowledgeListRequest | null) => {
  const { data, error, mutate } = useKnowledgeList(params)
  const { trigger: saveOrUpdate, isMutating: isSaving } =
    useSaveOrUpdateKnowledge()
  const { trigger: deleteKnowledge, isMutating: isDeleting } =
    useDeleteKnowledge()
  const { trigger: toggleState, isMutating: isToggling } =
    useToggleKnowledgeState()

  /**
   * 更新知识库
   * @param knowledgeData 知识库数据（必须包含id）
   * @returns Promise<KnowledgeBase>
   */
  const updateKnowledge = async (
    knowledgeData: SaveOrUpdateKnowledgeRequest
  ) => {
    const result = await saveOrUpdate(knowledgeData)
    return result
  }

  /**
   * 删除知识库
   * @param id 知识库ID
   * @returns Promise<boolean>
   */
  const removeKnowledge = async (id: string) => {
    const result = await deleteKnowledge({ id })
    // 操作成功后刷新列表
    await mutate()
    return result
  }

  /**
   * 切换知识库启用状态
   * @param id 知识库ID
   * @param status 知识库状态
   * @returns Promise<boolean>
   */
  const toggleKnowledgeStatus = async (id: string, status: KnowledgeStatus) => {
    const result = await toggleState({ id, status })
    // 操作成功后刷新列表
    await mutate()
    return result
  }

  return {
    // 列表数据
    knowledgeList: data?.knowledgeBases || [],
    isLoading: !data && !error,
    error,

    // 操作方法
    updateKnowledge,
    removeKnowledge,
    toggleKnowledgeStatus,

    // 加载状态
    isSaving,
    isDeleting,
    isToggling,

    // 刷新方法
    refresh: mutate,
  }
}
